import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, User as UserIcon, MapPin, Truck } from 'lucide-react'
import { getVolunteerById, VolunteerContactProfile } from '../api/volunteers'

const VolunteerProfilePage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [volunteer, setVolunteer] = useState<VolunteerContactProfile | null>(null)

  useEffect(() => {
    const run = async () => {
      if (!id) {
        setError('Volunteer ID is missing')
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError(null)
        const data = await getVolunteerById(id)
        setVolunteer(data)
      } catch (e: any) {
        setError(e?.message || 'Failed to load volunteer profile')
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/3" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mr-4">
                <UserIcon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {volunteer ? `${volunteer.firstName} ${volunteer.lastName}` : 'Volunteer'}
                </h1>
                <p className="text-sm text-gray-600">Volunteer Profile</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">Unable to load profile</p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            ) : volunteer ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <a className="hover:underline" href={`mailto:${volunteer.email}`}>
                      {volunteer.email}
                    </a>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <a className="hover:underline" href={`tel:${volunteer.phone}`}>
                      {volunteer.phone}
                    </a>
                  </div>
                </div>

                {(volunteer.volunteerProfile?.city || volunteer.volunteerProfile?.state) && (
                  <div className="flex items-center text-sm text-gray-700">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                    <span>
                      {[volunteer.volunteerProfile?.city, volunteer.volunteerProfile?.state].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}

                {volunteer.volunteerProfile?.vehicleType && (
                  <div className="flex items-center text-sm text-gray-700">
                    <Truck className="w-4 h-4 mr-2 text-gray-400" />
                    <span>Vehicle: {volunteer.volunteerProfile.vehicleType}</span>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Contact details are visible to suppliers and food banks to coordinate deliveries.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">Volunteer not found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VolunteerProfilePage


